import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
  CheckCircle as ConfirmIcon,
  Cancel as CancelIcon,
  CompareArrows as DesviosIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import produccionService, {
  EstadoProduccionDto,
  ProduccionOrdenListItemDto,
} from '../../services/produccionService';

const esFinalizada = (estado?: string | null) =>
  (estado ?? '').toLowerCase().replace(/\s/g, '').includes('finaliz');

const esAnulada = (estado?: string | null) =>
  (estado ?? '').toLowerCase().replace(/\s/g, '').includes('anulad');

const OrdenesProduccion: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<ProduccionOrdenListItemDto[]>([]);
  const [estados, setEstados] = useState<EstadoProduccionDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [codigoEstado, setCodigoEstado] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async (pageNum: number, pageSize: number, search: string, estado: number | '') => {
      try {
        setLoading(true);
        const data = await produccionService.getOrdenes({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
          codigoEstado: estado === '' ? undefined : estado,
        });
        setItems(data.items);
        setTotalCount(data.totalCount);
      } catch {
        enqueueSnackbar('Error al cargar órdenes de producción', { variant: 'error' });
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    produccionService.getEstados().then(setEstados).catch(() => setEstados([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchData(page, rowsPerPage, searchTerm, codigoEstado), searchTerm ? 300 : 0);
    return () => clearTimeout(t);
  }, [page, rowsPerPage, searchTerm, codigoEstado, fetchData]);

  const handleIniciar = async (id: number) => {
    try {
      await produccionService.iniciar(id);
      enqueueSnackbar('Orden iniciada', { variant: 'success' });
      fetchData(page, rowsPerPage, searchTerm, codigoEstado);
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'No se pudo iniciar la orden';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleAnular = async (id: number) => {
    if (!window.confirm('¿Anular esta orden de producción?')) return;
    try {
      await produccionService.anular(id, 'Anulación desde listado');
      enqueueSnackbar('Orden anulada', { variant: 'success' });
      fetchData(page, rowsPerPage, searchTerm, codigoEstado);
    } catch (error: unknown) {
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'No se pudo anular la orden';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Órdenes de producción
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/produccion/ordenes/nuevo')}
            >
              Nueva orden
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder="Buscar por código, artículo o descripción"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Estado"
              value={codigoEstado}
              onChange={(e) => {
                const v = e.target.value;
                setCodigoEstado(v === '' ? '' : Number(v));
                setPage(0);
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {estados.map((e) => (
                <MenuItem key={e.codigoEstado} value={e.codigoEstado}>
                  {e.estado}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nº</TableCell>
                  <TableCell>Artículo</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell align="right">Producida</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Inicio</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.codigoProduccion} hover>
                    <TableCell>{row.codigoProduccion}</TableCell>
                    <TableCell>
                      {row.codigoArticulo} — {row.descripcion}
                    </TableCell>
                    <TableCell align="right">{row.cantidad ?? 0}</TableCell>
                    <TableCell align="right">{row.cantidadProducida ?? 0}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.estado ?? '—'}
                        color={esAnulada(row.estado) ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {row.fechaInicio ? new Date(row.fechaInicio).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {row.puedeEditar && (
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/produccion/ordenes/${row.codigoProduccion}`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {row.puedeEditar && row.estado?.toLowerCase().includes('borrador') && (
                        <IconButton size="small" color="primary" onClick={() => handleIniciar(row.codigoProduccion)}>
                          <PlayIcon fontSize="small" />
                        </IconButton>
                      )}
                      {row.puedeConfirmar && (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => navigate(`/produccion/ordenes/${row.codigoProduccion}/confirmar`)}
                        >
                          <ConfirmIcon fontSize="small" />
                        </IconButton>
                      )}
                      {esFinalizada(row.estado) && (
                        <Tooltip title="Desvíos de consumo">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/produccion/ordenes/${row.codigoProduccion}/desvios`)}
                          >
                            <DesviosIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {row.puedeAnular && (
                        <IconButton size="small" color="error" onClick={() => handleAnular(row.codigoProduccion)}>
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No hay órdenes para mostrar.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Filas"
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrdenesProduccion;
